import type { CodeableConcept } from '@medplum/fhirtypes'
import { compact, uniq } from 'lodash-es'
import { CHIP_COLORS, chipColor } from '#src/chipColors.ts'
import {
  codeableConcept,
  getCodingsFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  NOTE_LABEL_TAG_SYSTEM,
  PRACTITIONER_ROLE,
  VISIT_NOTE_CATEGORY,
} from '#src/codeSystems.ts'
import { invariant } from '#src/invariant.ts'
import { NoteLabel } from '#src/models/noteLabel.ts'
import { ROLES, Role } from '#src/roles.ts'
import { codingToString, type QueryTypes } from '#src/search.ts'

/**
 * A hard-coded visit-note category. Unlike note labels (user-created `Basic`
 * resources), categories are a fixed vocabulary baked into the codebase. They
 * render in the note header as colored chips identical to labels but persist to
 * `Composition.category[]` (token-searchable as `system|code`) rather than
 * `meta.tag`.
 */
export interface VisitNoteCategoryDefinition {
  code: string
  system: string
  display: string
  /** Chip color, drawn from the shared label palette for visual parity. */
  color: string
}

const PALETTE = CHIP_COLORS.map((c) => c.color)
const colorAt = (index: number): string => PALETTE[index % PALETTE.length]

/**
 * The `system|code` token a category serializes to inside a combobox value and
 * a Medplum token search. Delegates to the canonical `codingToString` so the
 * registry keys and `CompositionNote.categories` read use one serializer.
 */
export function visitNoteCategoryToken(def: {
  system: string
  code: string
}): string {
  return codingToString(def)
}

/**
 * Assessment-kind categories — an explicit, hand-listed starter set under the
 * dedicated VisitNoteCategory system. Exported as the authoritative source for
 * "is this an assessment?" surfaces (e.g. the Notes page Assessments tab), so
 * that set grows automatically as kinds are added here.
 */
export const ASSESSMENT_VISIT_NOTE_CATEGORIES: VisitNoteCategoryDefinition[] = [
  {
    code: 'initial-assessment',
    system: VISIT_NOTE_CATEGORY,
    display: 'Initial assessment',
    color: chipColor('Purple'),
  },
  {
    code: '180-day-assessment',
    system: VISIT_NOTE_CATEGORY,
    display: '180 Day Reassessment',
    color: chipColor('Teal'),
  },
  {
    code: 'change-of-condition-assessment',
    system: VISIT_NOTE_CATEGORY,
    display: 'Change of Condition Assessment',
    color: chipColor('Green'),
  },
  {
    code: 'extended-loc-assessment',
    system: VISIT_NOTE_CATEGORY,
    display: 'Extended LOC Assessment',
    color: chipColor('Orange'),
  },
  {
    code: 'change-of-residence-assessment',
    system: VISIT_NOTE_CATEGORY,
    display: 'Change of Residence Assessment',
    color: chipColor('Yellow'),
  },
]

/**
 * Role categories — one per role, DERIVED from the single `roles.ts` source so
 * the vocabulary can never drift from `ROLES`. Each reuses the canonical
 * `PRACTITIONER_ROLE` coding (code + display), making "all OT notes" a plain
 * token search on `Composition.category`.
 */
const ROLE_CATEGORIES: VisitNoteCategoryDefinition[] = Object.values(Role).map(
  (role, index) => {
    const coding = ROLES[role].coding?.find(
      (c) => c.system === PRACTITIONER_ROLE
    )
    invariant(
      coding?.code && coding.display,
      `Role ${role} is missing a PRACTITIONER_ROLE coding`
    )
    return {
      code: coding.code,
      system: PRACTITIONER_ROLE,
      display: coding.display,
      color: colorAt(index),
    }
  }
)

/** The full hard-coded category registry: assessment kinds + every role. */
export const VISIT_NOTE_CATEGORIES: VisitNoteCategoryDefinition[] = [
  ...ASSESSMENT_VISIT_NOTE_CATEGORIES,
  ...ROLE_CATEGORIES,
]

const BY_TOKEN = new Map(
  VISIT_NOTE_CATEGORIES.map((def) => [visitNoteCategoryToken(def), def])
)

const ASSESSMENT_CATEGORY_TOKENS = new Set(
  ASSESSMENT_VISIT_NOTE_CATEGORIES.map(visitNoteCategoryToken)
)

/**
 * Whether a `system|code` token is one of the assessment-kind categories (as
 * opposed to a role category or a note label). Used to narrow a Composition's
 * `Composition.category[]` tokens to the assessment set.
 */
export function isAssessmentVisitNoteCategoryToken(token: string): boolean {
  return ASSESSMENT_CATEGORY_TOKENS.has(token)
}

export function isRoleVisitNoteCategoryToken(token: string): boolean {
  return (
    isVisitNoteCategoryToken(token) &&
    !isAssessmentVisitNoteCategoryToken(token)
  )
}

export function resolveAssessmentKindToken(value: string): string {
  if (isAssessmentVisitNoteCategoryToken(value)) {
    return value
  }
  const def = ASSESSMENT_VISIT_NOTE_CATEGORIES.find((c) => c.code === value)
  invariant(def, `Unknown assessment kind: ${value}`)
  return visitNoteCategoryToken(def)
}

export function resolveDisciplineToken(value: string): string {
  if (isRoleVisitNoteCategoryToken(value)) {
    return value
  }
  const def = VISIT_NOTE_CATEGORIES.find(
    (c) => c.system === PRACTITIONER_ROLE && c.code === value
  )
  invariant(def, `Unknown discipline: ${value}`)
  return visitNoteCategoryToken(def)
}

/** Resolve a `system|code` token to its category definition, if registered. */
export function visitNoteCategoryByToken(
  token: string
): VisitNoteCategoryDefinition | undefined {
  return BY_TOKEN.get(token)
}

/** Whether a combobox value / coding token is a registered visit-note category. */
export function isVisitNoteCategoryToken(token: string): boolean {
  return BY_TOKEN.has(token)
}

/**
 * Read the registry visit-note category tokens (`system|code`) present in a
 * `CodeableConcept[]` field — e.g. `Composition.category` or `Library.topic`.
 * Every non-registry coding (bucket codings, unknown vocabularies) is ignored.
 * Shared verbatim by `CompositionNote`, `NoteTemplate`, and `Assessment` so the
 * categories contract has exactly one implementation.
 */
export function readVisitNoteCategoryTokens(
  concepts: CodeableConcept[] | undefined
): string[] {
  return (getCodingsFromCodeableConcept(concepts) ?? [])
    .map(codingToString)
    .filter(isVisitNoteCategoryToken)
}

/**
 * Merge selected category tokens into an existing `CodeableConcept[]`. Preserves
 * every non-registry coding — notably the bucket coding (`Note`, `Assessment`,
 * `Incident`) that `isInstance`/routing depends on — and owns ONLY the
 * registry-category entries, replacing whatever set was there before.
 */
export function mergeVisitNoteCategoryTokens(
  concepts: CodeableConcept[] | undefined,
  tokens: string[] | undefined
): CodeableConcept[] {
  const preserved = (concepts ?? []).filter(
    (cc) => !cc.coding?.some((c) => isVisitNoteCategoryToken(codingToString(c)))
  )
  const added = compact(
    uniq(tokens ?? []).map((token) => {
      const def = visitNoteCategoryByToken(token)
      return def
        ? codeableConcept({
            system: def.system,
            code: def.code,
            display: def.display,
          })
        : undefined
    })
  )
  return [...preserved, ...added]
}

/**
 * Build a Medplum `_filter` query fragment matching Compositions that carry ANY
 * of the selected note labels or visit-note categories (logical OR across the
 * union). Labels live in `meta.tag` (searched via `_tag`); categories live in
 * `Composition.category[]` (searched via `category`).
 *
 * A single picker emits both kinds as one flat `string[]` — labels as
 * `Basic/<id>` reference strings, categories as `system|code` tokens — so this
 * splits them back apart by shape. Cross-parameter OR (a note matching label X
 * OR category Y) isn't expressible with plain ANDed search params, so the
 * clauses are combined into one `_filter` expression. Returns an empty query
 * when nothing resolves to a label or category (no server-side narrowing).
 */
export function noteLabelCategoryFilter(selected: string[]): QueryTypes {
  const clauses = selected.flatMap((value) => {
    if (isVisitNoteCategoryToken(value)) {
      return [`category eq ${value}`]
    }
    if (NoteLabel.isValidReferenceString(value)) {
      return [`_tag eq ${NOTE_LABEL_TAG_SYSTEM}|${value}`]
    }
    return []
  })
  return clauses.length > 0 ? { _filter: `(${clauses.join(' or ')})` } : {}
}

export function assessmentKindCategoryFilter(): QueryTypes {
  return noteLabelCategoryFilter(
    ASSESSMENT_VISIT_NOTE_CATEGORIES.map(visitNoteCategoryToken)
  )
}

export function andQueryFilters(
  ...queries: { _filter?: QueryTypes['_filter'] }[]
): QueryTypes {
  const clauses = queries
    .map((query) => query._filter)
    .filter((clause): clause is string => typeof clause === 'string')
  return clauses.length > 0 ? { _filter: clauses.join(' and ') } : {}
}

export function isAssessmentsTabCategories(tokens: string[]): boolean {
  return tokens.some(isAssessmentVisitNoteCategoryToken)
}
